import type { FollowUpCategoryTypes } from '@/shared/api/graphql/account';
import type { RpmEnrolledAccounts } from '@/shared/api/graphql/remotePatientMonitoring';
import type { RelyingPartyAdminData } from '@/shared/lib/types/administration';
import type { BillingHoldInfo } from '@/shared/lib/types/billingHold';
import type { TruentityDateInput } from '@/shared/lib/types/date';
import type { AccountAssignmentType, AccountType, RelyingParty, RelyingPartyAdminType, RelyingPartyType } from '@/shared/lib/types/graphql';
import type { PatientsData } from '@/shared/lib/types/patients';
import type { SplitActionButton } from '@/shared/ui/components/MuiMenu';
import type { JSONData } from '@/shared/ui/JsonKit';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import type { Moment } from 'moment';
import type { ShowFn } from 'mui-modal-provider';
import type React from 'react';
import type { FollowUpRemindersTypes } from './accountProfile';
import type { Role } from './admin';
import type { TagType } from './tag';

export enum RpmMonitoringActivityTypes {
  CareActivity,
  Encounter
}

export const APPLE_NAME = 'apple';

export type HealthTypeTab = {
  a11yLabel: string;
  label: string;
  path: string;
  disabled: boolean;
  icon: React.JSX.Element;
};

export type RpmStatusTypeTab = {
  status: string;
  label: string;
  /** The label stacked one line per entry, where a long label would otherwise widen the tab row. */
  labelLines?: readonly string[];
  path: 'candidate' | 'priorityCandidate' | 'scheduledForEnrollment' | 'enrolled' | 'unenrolled';
  icon: React.JSX.Element;
  includeMetrics?: boolean;
};

/**
 * Tabs on the provider-approvals screen. Deliberately separate from RpmStatusTypeTab: that type's
 * `path` union is the key set of care-management's `columnVisibility` Record, and these paths
 * ('schedule-for-review', 'in-review', ...) are a different set entirely.
 */
export type ProviderApprovalStatusTab = {
  status: string;
  label: string;
  path: string;
  icon: React.JSX.Element;
};

export type TabPanelDataType = {
  index: number;
  label: string;
};

export type AccountDevicesType = {
  id: string;
  name: string;
  brandName: string;
  typeName: string;
  recordedAt?: string;
  createdAt?: string;
};

export type AccountMedicalServiceEnrollmentSummary = {
  rpmEnrolled: boolean;
  ccmEnrolled: boolean;
  ccmBillable: boolean;
  rpmStatus?: string | null;
  ccmStatus?: string | null;
  rpmEnrolledAt?: string | null;
  rpmUnenrolledAt?: string | null;
  ccmEnrolledAt?: string | null;
  ccmUnenrolledAt?: string | null;
  rpmMonthToDateSecs: number;
  ccmMonthToDateSecs: number;
  monthYear: string;
};

export type ReadingType = {
  value: string;
  unit: string;
  key: string;
  status: StatusTypeFilter;
  recordedAt: string;
  createdAt: string;
};

export enum GatewayConnectivityTypes {
  CELLULAR = 'CELLULAR',
  BLUETOOTH = 'BLUETOOTH'
}

export type AccountsMonitoringDeviceType = {
  id: string;
  externalPatientId: string;
  hardwareId: string;
  hardwarePatientId: string;
  status: string;
  vendorStatus?: string | null;
  connectedOn?: string | null;
  lastMeasurementAt?: string | null;
  gatewayId: string;
  monitoringDeviceName: string;
  gatewayConnectivityType?: GatewayConnectivityTypes | null;
  formattedGatewayId: string;
  signalStrength: number;
  signalStrengthPercentage: number;
  lastGatewayCheckingTime: string;
  shippedAt: string;
  monitoringDevice: AccountDevicesType;
  latestReadings?: ReadingType[];
};

/**
 * The alert a reading raised, when it raised one. Carried on the reading itself so the readings
 * table can double as the alerts queue: which rows are alerts, whether they have been read, and
 * enough detail to open the Alert Review Follow-up window without a second fetch.
 */
export type ReadingAlertType = {
  id: string;
  label?: string;
  description: string;
  shortDescription: string;
  reading: string;
  isRead: boolean;
  readAt?: string;
  recordedAt?: string;
  immediateActions: JSONData;
  /**
   * Someone opened this alert in the review panel without submitting the review. Distinct from
   * `isRead` -- viewed but not read is an alert that was picked up and left, which is what the
   * table's "Opened" state reports.
   */
  lastViewedAt?: string | null;
  /** That viewer as "Name (adminId)". */
  lastViewedBy?: string | null;
};

export type TableReadingsType = {
  id: string;
  source: string;
  value: string;
  status: string;
  recordedAt: string;
  isBaseline: boolean;
  alert?: ReadingAlertType | null;
};

export enum RpmAlertsLabelTypes {
  CRITICALLY_HIGH = 'CRITICALLY_HIGH',
  CRITICALLY_LOW = 'CRITICALLY_LOW',
  HIGH = 'HIGH',
  LOW = 'LOW',
  RPM_SETUP = 'RPM_SETUP',
  NO_READINGS = 'NO_READINGS',
  MED_REGIMEN = 'MED_REGIMEN',
  REPORT_FAX_FAILED = 'REPORT_FAX_FAILED'
}

export enum StatusTypeFilter {
  CRITICALLY_HIGH = 'Critically High',
  HIGH = 'High',
  NORMAL = 'Normal',
  LOW = 'Low',
  CRITICALLY_LOW = 'Critically Low'
}

export enum CareActivityTypes {
  MONTHLY_CARE_MANAGEMENT = 'Monthly Care Management',
  SETUP = 'Setup'
}

export interface StatusColors {
  textColor: string;
  bgColor: string;
}

export enum VitalsHealthTypes {
  HeartRate = 'heartRate',
  BloodPressure = 'bloodPressure',
  BloodGlucose = 'bloodGlucose'
}

export enum UnitType {
  HeartRate = 'BPM',
  BloodPressure = 'SYS/DIA (mmHg)',
  BloodGlucose = 'mg/dL'
}

export enum BloodPressureType {
  Systolic = 'SYS',
  Diastolic = 'DIA'
}

export interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  otherProps?: unknown;
}

export type RpmAlertsCount = {
  criticallyHigh: number;
  criticallyLow: number;
  high: number;
  low: number;
  noReadings: number;
  rpmSetup: number;
  medRegimen: number;
  reportFaxFailed: number;
};

export type RPMAccountAlert = {
  id: string;
  account: {
    id: string;
    truentityId: string;
    user: {
      firstName: string;
      lastName: string;
    };
    birthDate: string;
    zipcode: string;
    phone: string;
    rpmEnrolledAt: string;
    rpmStatus: string;
    accountSummary?: {
      noReadingsReminderSentAt?: string;
    };
    tags: TagType[];
  };
  description: string;
  reading: string;
  shortDescription: string;
  label?: string;
  /**
   * The underlying reason, e.g. `CRITICALLY_HIGH_BLOOD_GLUCOSE`. Unlike `label`, which is only the
   * severity, this names the vital -- which is what tells a deep link which readings tab the alert
   * lives on. See `getVitalsHealthTypeForAlertType`.
   */
  alertType?: string | null;
  careActivity: CareActivityType;
  createdAt: string;
  recordedAt?: string;
  lastReadingAt?: string;
  isRead: boolean;
  immediateActions: JSONData;
  /** When this patient's chart was last opened in the portal, by someone who is not an observer. */
  lastPortalView?: string | null;
  /** That viewer as "Name (adminId)", or "Admin (adminId)" for a super admin. */
  lastPortalViewer?: string | null;
  /**
   * When this alert was last opened in the review panel. Distinct from `isRead`, which means the
   * review was submitted -- viewed but not read is an alert somebody picked up and left.
   */
  lastViewedAt?: string | null;
  /** That viewer, in the same form as `lastPortalViewer`. */
  lastViewedBy?: string | null;
};

export type RpmCandidateStatisticsType = {
  title: string;
  value: number;
};

export type ConsentType = {
  rpmParticipation: boolean;
  rpmProgramDescriptionAccepted: boolean;
  rpmShareDate: boolean;
  rpmCanCancelAnytime: boolean;
  rpmWillReturnDevicesOnCancellation: boolean;
};

export enum RpmStatusTypes {
  IS_CANDIDATE = 'IS_CANDIDATE',
  READY_FOR_REVIEW = 'READY_FOR_REVIEW',
  SCHEDULE_FOR_REVIEW = 'SCHEDULE_FOR_REVIEW',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ENROLLED = 'ENROLLED',
  SKIPPED = 'SKIPPED',
  UNENROLLED = 'UNENROLLED',
  SCHEDULED_FOR_ENROLLMENT = 'SCHEDULED_FOR_ENROLLMENT'
}

export enum RpmReportReviewTypes {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED'
}

export enum RpmApprovalStatusTypes {
  PENDING = 'PENDING',
  SCHEDULE_FOR_REVIEW = 'SCHEDULE_FOR_REVIEW',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  SKIPPED = 'SKIPPED'
}

export enum ConfirmDialogLabelTypes {
  REJECT = 'REJECT',
  APPROVE = 'APPROVE',
  SEND = 'SEND',
  SKIP = 'INCOMPLETE',
  SEND_FOR_REVIEW = 'SEND FOR REVIEW',
  RESEND_FOR_REVIEW = 'RESEND FOR REVIEW',
  SEND_SCHEDULE_FOR_REVIEW = 'SCHEDULE FOR REVIEW'
}
export const mappedDialogLabelToRpmStatus = {
  [ConfirmDialogLabelTypes.REJECT]: RpmApprovalStatusTypes.REJECTED,
  [ConfirmDialogLabelTypes.APPROVE]: RpmApprovalStatusTypes.APPROVED,
  [ConfirmDialogLabelTypes.SKIP]: RpmStatusTypes.SKIPPED
};

export enum RpmDeviceTypes {
  BPM = 'BPM',
  GLUCOMETER = 'GLUCOMETER'
}

export type RpmStatusCounts = {
  [key in RpmStatusTypes]?: number;
};

export type ContactType = {
  type: string;
  value: string;
  status?: string;
};

export type ProviderType = {
  id: string;
  individualFirstName: string;
  individualLastName: string;
  individualMiddleName?: string;
  name?: string;
  nameWithTitle?: string;
  npiNumber: string;
  title?: string;
  email?: string;
  phone?: string;
  fax?: string;
  hasValidSession?: boolean;
  deaNumber?: string;
  taxonomyCode?: string;
  specialty?: string | null;
  lastEncounterDate?: string | null;
  lastRxDate?: string | null;
  stateLicense?: string;
  contacts: ContactType[];
};

export enum ProviderSessionStatusTypes {
  SUCCESS = 'Success',
  FAILED = 'Fail'
}

/*
 * CONSTANTS USED FOR THE
 * DROPDOWNS
 */
export const BRAND_NAMES = [{ label: 'TENOVI', value: 'Tenovi' }];

export const DEVICE_TYPE_NAMES = [
  { label: RpmDeviceTypes.BPM, value: RpmDeviceTypes.BPM.toLowerCase() },
  { label: RpmDeviceTypes.GLUCOMETER, value: RpmDeviceTypes.GLUCOMETER.toLowerCase() }
];

export enum StatusType {
  Initial = 'Initial',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Removed = 'Removed'
}

export type CheckboxStateType = {
  checked: boolean;
  disabled: boolean;
};

export enum ConfigOptionTypes {
  Provider = 'Provider',
  Patient = 'Patient'
}

export enum ActivityLogType {
  COMMUNICATION_SUMMARY = 'communication-summary',
  READINGS_REVIEW_SUMMARY = 'readings-review-summary',
  REPORTS_SUMMARY = 'reports-summary',
  REPORT_COMMUNICATION_SUMMARY = 'report-communication-summary'
}

export type RefactorActivityLogType = {
  id: string;
  logText: string;
  activityTime?: string | Date;
  activityType?: string;
  performedByName?: string;
  children?: { id: string; label: string; labelDesc?: number }[];
};

export type ActivityLog = {
  id: string;
  logText: string;
  account: AccountType;
  activityType: string;
  activityTime: string;
  encounterId: number;
  careActivityId: null | string;
  activityEndTime: null | string;
  timeTakenSecs: null | number;
  createdAt: string;
};

export interface MonthYearType {
  year: string;
  months: string[];
}

export interface AccountNoteType {
  id: string;
  title: string;
  modeOfCapture: string;
  note: string;
  shareWithProvider: boolean;
  pinToProfile: boolean;
  isArchived: boolean;
  createdAt: string;
}

export type medicationType = {
  title: string;
  id: string;
  note: string;
};

export interface CareActivityType {
  id: string;
  title: string;
  subTitle?: string;
  relyingParty?: RelyingPartyType;
  notes: string | null;
  category: string;
  subType: string;
  type?: ActivityMainTypeEnum | string | null;
  isEncounter: boolean;
  totalTimeSpentSecs: number;
  contactMode?: string;
  readingType?: RpmDeviceReadingType | null;
  createdAt: string;
  startedAt: string;
  endedAt: string | null;
  performedBy?: Pick<RelyingPartyAdminType, 'user'> | null;
}

export interface VitalsDataAvailabilityType {
  isBloodGlucoseDataAvailable: boolean;
  isBloodPressureDataAvailable: boolean;
  isHeartRateDataAvailable: boolean;
}

export interface RpmPatientPrioritizationReportType {
  id: string;
  patientName: string;
  phone: string;
  rpmPriorityScore: number;
  dateOfBirth: string;
  healthPlan: string;
  insurancePlan: string;
  medications: string;
}

export interface RpmReportsType {
  id: string;
  status: string;
  monthYear?: string;
  reportStartAt: string;
  reportEndAt: string;
  reportedDocS3Key: string;
  lastReportedOn: string;
  dailySendStatus?: string;
  statusUpdatedAt?: string;
  rpmProviderActivities?: RpmProviderActivitiesType[];
  reviewProviders?: ReviewProviderType[];
  account: AccountType;
  rpmAccountReviewWorkflow?: RpmAccountReviewWorkflowType;
  relyingParty?: Partial<RelyingParty>;
  summaryReportS3Key?: string;
  reportDownloadUrl?: string;
  printReportDownloadUrl?: string;
  printReportedDocS3Key?: string;
}

export interface RpmAccountReviewWorkflowType {
  id: string;
  signOffAt?: string;
  completedAt?: string;
  rpmAccountReviewNotes: PatientReviewNoteType[];
  provider?: ProviderType;
  rpmReport: RpmReportsType;
  careActivity?: Partial<CareActivityType>;
  reportS3Key?: string;
}

export interface RpmProviderActivitiesType {
  id: string;
  status?: string;
  performedAt: string | null;
  createdAt: string;
  requestedAt?: string;
  approvalRequestedAt?: string;
  rpmStatusUpdatedAt?: string;
  rpmReport?: RpmReportsType;
  account?: AccountType;
}

export type ParsedDeviceType = Pick<AccountsMonitoringDeviceType, 'id' | 'latestReadings' | 'monitoringDevice'>;

export type RpmAccountsType = Pick<
  PatientsData,
  | 'id'
  | 'truentityId'
  | 'user'
  | 'rpmEnrolledAt'
  | 'rpmUnenrolledAt'
  | 'lastMedrecDate'
  | 'rpmApprovalStatus'
  | 'rpmSignOffStatus'
  | 'healthPlan'
  | 'setting'
  | 'lastContacted'
  | 'rpmStatus'
  | 'phone'
  | 'tags'
> &
  Partial<
    Pick<
      PatientsData,
      | 'birthDate'
      | 'zipcode'
      | 'phone'
      | 'doNotCall'
      | 'accountsAssignments'
      | 'accountsFollowupReminders'
      | 'accountMetric'
      | 'billingHoldActive'
      | 'activeBillingHoldReasons'
      | 'visibleBillingHolds'
      | 'eligibilityReview'
      | 'smsOptOut'
    >
  >;

export type RpmAccountSnapshotType = RpmAccountsType &
  Partial<
    Pick<
      RpmEnrolledAccounts,
      | 'isRpmReportAvailable'
      | 'monthlyTimeSpent'
      | 'monthlyTotalReadings'
      | 'readingBasedCategoryName'
      | 'activityBasedCategoryName'
      | 'followupVisitDueStatus'
      | 'isLastVisitAsync'
      | 'lastFollowupVisitOn'
      | 'lastCompletedFollowupVisitOn'
      | 'lastActivityAt'
      | 'lastActivityDoneBy'
      | 'lastLogBpAt'
      | 'lastLogBgAt'
      | 'lastLogHrAt'
    >
  > &
  Partial<{
    devices: ParsedDeviceType[];
    // Combined Care Management page extras (populated by getRpmAccountSnapshot when the
    // combined query supplies them; undefined for the legacy RPM query).
    ccmStatus: string | null;
    rpmTime: number | null;
    ccmTime: number | null;
  }>;

export const ReportCommunicationActivityLogTypes = {
  RPM_MONTHLY_REPORT_SENT: 'Rpm::MonthlyReport::Sent',
  RPM_MONTHLY_REPORT_NOT_SENT: 'Rpm::MonthlyReport::NotSent',
  RPM_REPORT_SEND_NOW: 'Rpm::MonthlyReport::SendNow',
  UPDATE_REPORT_DAILY_SEND_STATUS_TO_SCHEDULE: 'Rpm::ReportDailySendStatus::ScheduleForSending',
  UPDATE_REPORT_DAILY_SEND_STATUS_TO_SENT: 'Rpm::ReportDailySendStatus::Sent',
  UPDATE_REPORT_DAILY_SEND_STATUS_TO_READY_FOR_SENDING: 'Rpm::ReportDailySendStatus::ReadyToSend',
  RPM_REPORT_FAX_SENT_TO_QUEUE: 'Rpm::Report::FaxSentToQueue',
  RPM_REPORT_FAX_SENT: 'Rpm::Report::FaxSent',
  RPM_REPORT_FAX_NOT_SENT: 'Rpm::Report::FaxNotSent'
};

export const ReportActivityLogTypes = {
  RPM_REPORT_FINALIZED: 'Rpm::MonthlyReport::Finalized',
  RPM_REPORT_NOT_FINALIZED: 'Rpm::MonthlyReport::NotFinalized',
  RPM_REPORT_GENERATED: 'Rpm::MonthlyReport::Generated',
  RPM_REPORT_NOT_GENERATED: 'Rpm::MonthlyReport::NotGenerated',
  RPM_REPORT_REGENERATED: 'Rpm::MonthlyReport::ReGenerated',
  RPM_REPORT_NOT_REGENERATED: 'Rpm::MonthlyReport::NotReGenerated'
};

export const ReadingsActivityLogTypes = {
  RPM_DAILY_REMINDER_SMS_SENT: 'Rpm::DailyReminderSMS::Sent'
};

export enum MedConsultStatusType {
  IN_PROGRESS = 'IN_PROGRESS',
  ENDED = 'ENDED',
  ARCHIVED = 'ARCHIVED'
}

export interface Diagnosis {
  id: string;
  code: string;
  name: string;
  category?: string;
  system?: string;
  isChronic?: boolean;
  subscribedToMedicalSubService?: boolean;
}

export enum GLOBAL_LOOKUP_TYPES {
  UNAVAILABLE = 'UNAVAILABLE',
  ENROLL = 'ENROLL',
  REDIRECT = 'REDIRECT'
}
export enum DiagnosisCategories {
  DIABETES = 'DIABETES',
  HYPERTENSION = 'HYPERTENSION',
  CHF = 'CHF',
  HEART_DISEASE = 'HEART_DISEASE',
  CKD = 'CKD',
  COPD = 'COPD',
  ASTHMA = 'ASTHMA',
  DYSLIPIDEMIA = 'DYSLIPIDEMIA',
  OBESITY = 'OBESITY',
  DEPRESSION = 'DEPRESSION',
  RHEUMATOID_ARTHRITIS = 'RHEUMATOID_ARTHRITIS',
  OSTEOARTHRITIS = 'OSTEOARTHRITIS',
  OTHER = 'OTHER'
}

// One task within a staged goal — matches the GraphQL EncounterGoalTaskInput shape
export type StagedGoalTaskInput = {
  templateId: string | null;
  recordId: string | null;
  content: string;
  isSelected: boolean;
  isRemoved: boolean;
};

// One staged goal — matches the GraphQL EncounterGoalInput shape from buildPayload().
export type StagedGoalInput = {
  goalId?: string | null;
  accountGoalId?: string | null;
  isSelected: boolean;
  goalContent: string;
  patientTasks: StagedGoalTaskInput[];
  careTeamTasks: StagedGoalTaskInput[];
};

// One staged order toggle — matches the GraphQL AccountOrderInput shape.
export type StagedOrderInput = {
  orderType: string;
  orderChecked: boolean;
};

export type AmendmentStagedEdits = {
  monitoredConditions: DiagnosisCategories[] | null;
  stagedCcmDiagnoses: string[] | null;
  goals: StagedGoalInput[] | null;
  orders: StagedOrderInput[] | null;
};

export enum DiagnosisSystems {
  ICD = 'icd',
  SNOMED = 'snomed',
  OTHER = 'Other'
}

export enum RpmDiagnosisCodes {
  DIABETES = 'E11.9',
  HYPERTENSION = 'I10'
}

export const RPM_DIAGNOSIS_CODES = new Set<string>(Object.values(RpmDiagnosisCodes));

export type AlertLimitsType = {
  bloodGlucose: AlertLimitType;
  heartRate: AlertLimitType;
  sysBloodPressure: AlertLimitType;
  diaBloodPressure: AlertLimitType;
};

export interface AlertLimitType {
  cLowValue: number | string;
  lowValue: number | string;
  highValue: number | string;
  cHighValue: number | string;
}
export enum RpmWorkflowTab {
  CLINICAL_SUMMARY = 'CLINICAL_SUMMARY',
  DEVICES = 'DEVICES',
  CONSENT_MANAGEMENT = 'CONSENT_MANAGEMENT',
  MEDICAL_RECONCILIATION = 'MEDICAL_RECONCILIATION',
  MEDICAL_CONSULTS = 'MEDICAL_CONSULTS',
  CARE_PLAN = 'CARE_PLAN',
  CONFIGURATION = 'CONFIGURATION'
}

export const RpmWorkflowTabNames: { [key in RpmWorkflowTab]: string } = {
  [RpmWorkflowTab.CLINICAL_SUMMARY]: 'Medical History',
  [RpmWorkflowTab.DEVICES]: 'Devices',
  [RpmWorkflowTab.CONSENT_MANAGEMENT]: 'Administration',
  [RpmWorkflowTab.MEDICAL_RECONCILIATION]: 'Medication Reconciliation',
  [RpmWorkflowTab.MEDICAL_CONSULTS]: 'Intake (HPI)',
  [RpmWorkflowTab.CARE_PLAN]: 'Assessment & Plan',
  [RpmWorkflowTab.CONFIGURATION]: 'Configuration'
};

export enum RpmSetupStatusTypes {
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN_PROGRESS'
}

export type RpmClaimsType = {
  account: AccountType;
  ehrId: string;
  description: string;
  diagnosesCodes: string;
  diagnosesNames: string;
  cptCode: string;
  dateOfService?: TruentityDateInput;
  relyingParty: RelyingPartyType;
  provider?: ProviderType;
  // A hold applied during this charge's billing month; holdReasons also carries warnings.
  onHold?: boolean;
  holdReasons?: BillingHoldInfo[];
};

export enum ClaimsCptCodes {
  RPM_SETUP_CODE = '99453',
  RPM_MONTHLY_MONITORING_CODE = '99457',
  RPM_MONTHLY_MONITORING_ADDITIONAL_CODE = '99458',
  RPM_DAILY_MONITORING_CODE = '99454'
}

export enum ProviderSessionStatus {
  STARTED = 'started',
  INVALID_OTP = 'invalid_otp',
  INVALID_ACCOUNT = 'invalid_account'
}

export type ClinicalRangesFormValues = {
  submitType?: GlobalAlertLimitsSubmitEnum;
  heartRateLimits: [number, number, number, number];
  bloodGlucoseLimits: [number, number, number, number];
  sysBloodPressureLimits: [number, number, number, number];
  diaBloodPressureLimits: [number, number, number, number];
};

export enum RpmConfigurationsParams {
  CLINICAL_RANGES = 'clinical_ranges',
  PATIENT_SETTINGS = 'patient_settings'
}

export enum GlobalAlertLimitsSubmitEnum {
  SAVE = 'SAVE',
  SAVE_AND_APPLY_TO_ALL = 'SAVE_AND_APPLY_TO_ALL'
}

export enum ProviderReviewTypeEnum {
  REPORT = 'REPORT',
  ACCOUNT = 'ACCOUNT'
}

export enum ReviewSubmitTypeEnum {
  SCHEDULE_FOR_REVIEW = 'SCHEDULE_FOR_REVIEW',
  SEND_NOW = 'SEND_NOW',
  READY_TO_SEND = 'READY_TO_SEND'
}

export enum RpmReportStatusTypeEnum {
  READY_FOR_SENDING = 'READY_FOR_SENDING',
  SCHEDULED_FOR_SENDING = 'SCHEDULED_FOR_SENDING',
  SENT = 'SENT',
  FAILED = 'FAILED'
}

export enum UpdateRpmReportStatusTypeEnum {
  REMOVE = 'REMOVE',
  READY_FOR_SENDING = 'READY_FOR_SENDING',
  SCHEDULED_FOR_SENDING = 'SCHEDULED_FOR_SENDING'
}

export type RpmReportsCommunicationType = {
  rpmReport: RpmReportsType;
  scheduledAt: string;
  scheduledMode: string;
};

export type ReviewProviderType = {
  provider: ProviderType;
  reportSendingContacts: RpmReportContactType[];
};

export enum ContactedType {
  PHONE = 'phone',
  IN_PERSON_VISIT = 'in_person_visit',
  FOLLOW_UP = 'follow_up',
  INITIAL_VISIT = 'initial_visit'
}

export enum ContactModeType {
  EMAIL = 'Email',
  TELEPHONE = 'Telephone',
  FAX = 'Fax'
}

export type RpmAlertsTabType = {
  label: string;
  path: string;
  value: RpmAlertTabEnum;
  columnVisibility: GridColumnVisibilityModel;
  defaultAlertTypes: { [key: string]: boolean };
};

export enum RpmAlertTabEnum {
  PATIENTS = 'PATIENTS',
  REPORTS = 'REPORTS'
}

export type RpmPharmacyPerformanceReportType = {
  relyingParty: RelyingPartyType;
  account: AccountType;
  monthlyReadingsCount: number;
  monthlyTimeSpentFormat: string;
  monthlyTimeSpentInMinutes: number;
  baselineReadings: ReadingType[];
  averageReadings: ReadingType[];
};

export type RpmAccountHealthTrackingType = {
  id: string;
  value: string;
  unit: string;
  startedAt: string;
  endedAt?: string;
  key: string;
  sourceType: string;
};

export enum AmendmentStatus {
  AMENDED = 'AMENDED'
}

export type RpmFollowupConsultType = {
  id: string;
  consultAt: string;
  signOffAt?: string;
  reviewProvider?: ProviderType;
  accountsFollowupReminder: Pick<
    FollowUpRemindersTypes,
    'id' | 'type' | 'followUpOn' | 'relyingPartyAdmin' | 'mode' | 'appointmentRemindersEnabled'
  >;
  reportS3Key?: string;
  status: string;
  statusUpdatedAt?: string;
  isTelehealthVisitLaunched: boolean;
  amendable?: boolean;
  amendmentStatus?: AmendmentStatus | null;
  lastAmendedAt?: string;
  amendmentCount?: number;
  createdAt: string;
  updatedAt: string;
};

export enum RpmHealthTrackingKeys {
  HEMOGLOBIN_A1C = 'HEMOGLOBIN_A1C'
}

export type RpmAccountSummaryType = {
  carePlanS3Key?: string;
  rpmEncounterS3Key?: string;
  signOffAt?: string;
  isCarePlanFilled?: boolean;
  amendable?: boolean;
  amendmentStatus?: AmendmentStatus | null;
  lastAmendedAt?: string;
  amendmentCount?: number;
};

export enum RpmAccountSmartGoalStatuses {
  PENDING = 'PENDING',
  MET = 'MET',
  PARTIALLY_MET = 'PARTIALLY_MET',
  NOT_MET = 'NOT_MET'
}

export type SmartGoalCategory = {
  category: string;
  subCategory: {
    categoryName: string;
    templates: SmartGoalTemplateType[];
  }[];
};

export type SmartGoalTemplateType = {
  id: string;
  templateName: string;
  templateText: string;
  category: string;
  subCategory: string;
  placeholders: string[];
  validationRules: JSONData;
};

export type AccountSmartGoalType = {
  id: string;
  account?: AccountType;
  relyingParty?: RelyingParty;
  relyingPartyAdmin?: RelyingPartyAdminType;
  goalContent: string;
  smartGoalTemplate?: SmartGoalTemplateType;
  status: string;
  statusChangeAt: string;
  userInput?: JSONData;
  isArchived: boolean;
  rpmFollowupConsult?: {
    id: string;
  };
  createdAt: string;
  updatedAt: string;
};

export enum AccountRpmMonitoredCondition {
  DIABETES = 'Diabetes',
  HYPERTENSION = 'Hypertension',
  DIABETES_AND_HYPERTENSION = 'Diabetes and Hypertension',
  UNASSIGNED = 'Unassigned'
}

export enum RpmSignOffStatus {
  TELEHEALTH_INITIAL_VISIT_APPROVED = 'TELEHEALTH_INITIAL_VISIT_APPROVED',
  TELEHEALTH_FOLLOWUP_VISIT_APPROVED = 'TELEHEALTH_FOLLOWUP_VISIT_APPROVED',
  ASYNCHRONOUSLY_APPROVED = 'ASYNCHRONOUSLY_APPROVED'
}

export type AccountOrderType = {
  account: AccountType;
  provider: ProviderType;
  orderChecked: boolean;
  orderDatetime: string;
  orderPdf: string;
  orderType: string;
};

export enum AccountOrderTypes {
  RPM_BLOOD_PRESSURE_AND_HEART_RATE = 'RPM_BLOOD_PRESSURE_AND_HEART_RATE',
  RPM_BLOOD_SUGAR = 'RPM_BLOOD_SUGAR',
  CHRONIC_CARE_MANAGEMENT = 'CHRONIC_CARE_MANAGEMENT'
}

export type TemplateInput = {
  key: string;
  value: string;
};

export type ParsedTemplateResult = {
  templateId: string;
  template: SmartGoalTemplateType;
  inputs: TemplateInput[];
  isValidated?: boolean;
  isCompleted?: boolean;
};

export type CustomGoalType = {
  id: string;
  value: string;
  isCompleted?: boolean;
};

export type TabBasedActiveAdminsType = {
  tabType: RpmWorkflowTab;
  path: string;
  admins: Record<Exclude<Role, Role.ADT_ADMIN>, number>;
};

export type RpmWorkflowTabType = {
  label: string;
  path: string;
  type?: RpmMonitoringActivityTypes;
  tabType: RpmWorkflowTab;
  tabReadOnlyRoleTypes: Role[];
  accessDisableRoleTypes: Role[];
  disableProgress?: boolean;
};

export enum RpmFollowupConsultStatusType {
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING'
}

export type RpmFollowupConsultTabType = {
  id: string;
  type: string;
  status: string;
  isManualStatusUpdate: boolean;
  createdAt: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isProgressDisabled?: boolean;
  shouldRefreshTab?: boolean;
};

export enum RpmFollowupConsultFilterType {
  ALL = 'ALL',
  INDIVIDUAL = 'INDIVIDUAL'
}

export type KpiFilterType = {
  filterBy: string;
  truentityIds: string[];
};

export enum CareActivityContactModeType {
  PHONE_CALL = 'PHONE_CALL',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  IN_PERSON_VISIT = 'IN_PERSON_VISIT'
}

export enum ActivityMainCategoryType {
  MONTHLY_CARE_MANAGEMENT = 'Monthly Care Management',
  SETUP = 'Setup'
}

export enum ActivityMainTypeEnum {
  RPM = 'RPM',
  CCM = 'CCM'
}

export type ReportMonth = {
  id?: string;
  label: string;
  docS3Key?: string;
  printDocS3Key?: string;
  rpmAccountReviewWorkflow?: RpmAccountReviewWorkflowType;
  summaryReportS3Key?: string;
  monthYear?: string;
} & SplitActionButton;

export enum ReviewNoteCategoryType {
  INTRODUCTORY = 'INTRODUCTORY',
  ASSESSMENT = 'ASSESSMENT',
  INTERVENTION = 'INTERVENTION',
  GENERAL = 'GENERAL'
}

export type PatientReviewNoteType = {
  id: string;
  title?: string;
  note: string;
  authorType: string;
  noteCategory: ReviewNoteCategoryType | null;
  relyingPartyAdmin: Partial<RelyingPartyAdminType>;
  rpmAccountReviewWorkflow: RpmAccountReviewWorkflowType;
  accountsFollowupReminders: FollowUpRemindersTypes[];
  replies: PatientReviewNoteType[];
  createdAt: string;
};

export type PatientReviewWorkflowType = {
  signOffAt?: string;
  completedAt?: string;
  rpmAccountReviewNotes: PatientReviewNoteType[];
};

export enum VisitTypeEnum {
  FOLLOWUP_VISIT = 'FOLLOWUP_VISIT',
  INITIAL_VISIT = 'INITIAL_VISIT'
}

export enum RpmReportReviewTypeEnum {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SIGNED_OFF = 'SIGNED_OFF'
}

export type TemplateType = {
  templateString: string;
  dynamicFields: string;
};

export type PatientReviewTemplateType = {
  category: string;
  templates: TemplateType[];
};

export type DynamicTemplateType = {
  templateId: string;
  templateText: string;
  validationRules?: JSONData;
  inputs: TemplateInput[];
};

export type BaseFollowUpEditModalType = {
  followUp: FollowUpRemindersTypes;
  showModal: ShowFn;
  accountAssignments: (
    | {
        relyingPartyAdmin: RelyingPartyAdminData;
      }
    | AccountAssignmentType
  )[];
  onComplete?: () => void;
};

export type GeneralFollowUpEditModalType = BaseFollowUpEditModalType & {
  category: FollowUpCategoryTypes.GENERAL;
  truentityId: string | undefined;
  rpmStatus: string | undefined;
  doNotCall: boolean;
  patientPhone?: string;
};

// Every category EXCEPT General, which has its own shape above. These all route to
// FollowUpTaskAddEditDialog, whose own category prop is the full enum, so widening this union is
// all an added category needs.
export type TaskFollowUpEditModalType = BaseFollowUpEditModalType & {
  category: FollowUpCategoryTypes.ALERT_REVIEW | FollowUpCategoryTypes.CHART_REVIEW | FollowUpCategoryTypes.OBTAIN_CONSENT;
};

export type NavigationBlockStatusType = {
  isActivityLogged: boolean;
  activityLoggedAt: Moment | null;
};

export enum RpmCareActivityTitleType {
  CARE_COORDINATION = 'Care Coordination',
  DEVICE_AND_READINGS = 'Device & Readings',
  MEDICATION_MANAGEMENT_SUPPORT = 'Medication Management Support'
}

export enum RpmCareActivitySubTitleType {
  REVIEW_OF_PATIENT_READINGS = 'Review of patient device readings',
  REVIEW_AND_SHARE_REPORTS = 'Review and sharing monthly monitoring report with PCP/Specialist',
  REVIEW_MEDICATION_REGIMEN = 'Medication regimen review based on device reading trends'
}

export enum RpmDeviceReadingType {
  BLOOD_PRESSURE = 'BLOOD_PRESSURE',
  BLOOD_GLUCOSE = 'BLOOD_GLUCOSE',
  HEART_RATE = 'HEART_RATE',
  ALL_DEVICES = 'ALL_DEVICES'
}

export const RPM_DEVICE_READING_LABELS: Record<RpmDeviceReadingType, string> = {
  [RpmDeviceReadingType.BLOOD_PRESSURE]: 'Blood Pressure',
  [RpmDeviceReadingType.BLOOD_GLUCOSE]: 'Blood Glucose',
  [RpmDeviceReadingType.HEART_RATE]: 'Heart Monitor',
  [RpmDeviceReadingType.ALL_DEVICES]: 'All Devices'
};

export enum FollowupVisitDueStatusTypes {
  NONE_DUE = 'NONE_DUE',
  OVERDUE = 'OVERDUE',
  UPCOMING = 'UPCOMING',
  COMPLETED = 'COMPLETED'
}

export enum RpmVisitDocumentType {
  VISIT_ENCOUNTER_NOTE = 'VISIT_ENCOUNTER_NOTE',
  CLINICAL_SUMMARY = 'CLINICAL_SUMMARY',
  COMPREHENSIVE_VISIT_NOTE = 'COMPREHENSIVE_VISIT_NOTE'
}

export enum RpmMonthlyTaskFilter {
  OUTDATED_CONTACTS = 'OUTDATED_CONTACTS',
  OUTDATED_MEDREC = 'OUTDATED_MEDREC'
}

export enum RpmBillingFilter {
  BILLING_PAUSED = 'BILLING_PAUSED',
  BILLING_WARNING = 'BILLING_WARNING',
  // PSMs and Super Admins only (z93r0b9nj2): Unverified eligibility. It also places an Eligibility Unverified hold (z93r0b9pk0).
  ELIGIBILITY_UNVERIFIED = 'ELIGIBILITY_UNVERIFIED'
}

export type EnrolledAccountsFilterType = {
  status: FollowupVisitDueStatusTypes | RpmMonthlyTaskFilter | RpmBillingFilter;
  count: number;
};

export type RpmReportTemplateType = {
  value: string;
  validationRules: JSONData;
  userInput?: Array<{ name: string; value: string }>;
};

export type RpmReportContactType = ContactType & {
  id?: string;
  message?: string;
};

export enum RpmReportTypeEnum {
  PRINT = 'PRINT',
  COLOR = 'COLOR'
}

// Provider Workqueue report (query: getProviderWorkqueue)
export type ProviderWorkqueueItemType = {
  workqueueStatus: string;
  truentityId?: string | null;
  patientName?: string | null;
  pharmacyName?: string | null;
  visitType: string;
  rpmFollowupConsultId?: string | null;
  clinicalCareEncounterId?: string | null;
  signedOffAt?: string | null;
  lastAmendedAt?: string | null;
  rpmEnrolledAt?: string | null;
  ccmEnrolledAt?: string | null;
  currentEnrolledPrograms: string[];
  // Template generation the note was authored against. Null when the visit stores no note at all,
  // and on CCM Onboarding rows (which track an enrollment step rather than a note).
  noteVersion?: number | null;
  provider?: string | null;
  amendingProvider?: string | null;
  // When the provider signed off the async CCM onboarding encounter. Populated on the two async
  // CCM queues only, and null by definition on the provider queue (nothing there is signed yet).
  providerSignedOffAt?: string | null;
};

export type ProviderWorkqueueSummaryType = {
  incompleteCount: number;
  totalCount: number;
};

// Providers who signed or amended a note in the current scope. Drives the rendering-provider filter.
export type ProviderWorkqueueProviderType = {
  id: string;
  name?: string | null;
};

export type ProviderWorkqueueFilterOptionsInput = {
  relyingPartyId?: string | null;
  renderingProviderId?: string | null;
  signOffStartDate?: string;
  signOffEndDate?: string;
  hideCompleted?: boolean;
  visitType?: string | null;
  noteVersion?: string | null;
};
